/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { success, error } from '@/libs/notification';
import router from '@/router';
import {
  InventoryState,
  InventoryDataState,
  InventoryDetailsDataState,
  InventorySubmitDataState,
  InventoryPutDataState,
  InventoryDeleteDataState,
  InventoryDetailTableData,
  InventoryFilterForm
} from '@/types/inventory';

import { ElLoading } from 'element-plus';
import { handleAuthorizationError } from '@/libs/apiErrorHandler';
import { getAccessToken } from '@/libs/firebaseAuth';
import { getBaseUrl } from '@/libs/env';

export default {
  namespaced: true,
  state: {
    detailData: null,
    putData: null,
    depotId: null,
    depotName: null,
    inventoryDate: null,
    status: null
  },
  mutations: {
    setContent(state: InventoryState, payload: InventoryDataState[]) {
      state.listData = payload;
    },
    setDetailStatus(
      state: InventoryState,
      payload: Partial<InventoryDataState> & { status: number }
    ) {
      state.status = payload.status;
      if (payload.depotId != null) state.depotId = payload.depotId;
      if (payload.depotName != null) state.depotName = payload.depotName;
      if (payload.inventoryDate != null) {
        state.inventoryDate = payload.inventoryDate;
      }
    },
    setDetail(state: InventoryState, payload: InventoryDetailsDataState[]) {
      state.detailListData = payload;
    },
    setPrintTable(state: InventoryState, payload: InventoryDetailTableData[]) {
      state.printListData = payload;
    },
    setFilter(state: InventoryState, payload: InventoryFilterForm) {
      state.filterData = payload;
    }
  },
  actions: {
    async fetch(context: any) {
      await axios
        .get(getBaseUrl() + '/inventories', {
          headers: { auth: await getAccessToken() }
        })
        .then((response) => {
          context.commit('setContent', response.data);
        })
        .catch(async (e) => {
          if (e.response.status === 401) {
            await handleAuthorizationError();
          }
          error(e.response.status);
          router.push('/error/server');
        });
    },
    async submit(context: any, payload: InventorySubmitDataState) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .post(
          getBaseUrl() + '/inventories/confirm',
          {
            depotId: payload.depotId,
            inventoryDate: payload.inventoryDate
          },
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then((response) => {
          context.commit('setContent', response.data);
          loadingInstance.close();
          success('Success', '棚卸日の登録に成功しました。');
          router.push('/inventory');
        })
        .catch(async (e) => {
          if (e.response.status === 401) {
            await handleAuthorizationError();
          }
          error(e.response.status);
          router.push('/error/server');
        });
    },
    async put(context: any, payload: InventoryPutDataState) {
      await axios
        .put(
          getBaseUrl() + '/inventories',
          {
            depotId: payload.depotId,
            inventoryDate: payload.inventoryDate,
            itemId: payload.itemId,
            inventoryQty: payload.inventoryQty
          },
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .catch(async (e) => {
          if (e.response?.status === 401) {
            await handleAuthorizationError();
          } else if (e.response?.status && e.response.status < 500) {
            error(String(e.response.status));
            router.push('/inventory');
          } else {
            error(String(e.response?.status ?? 500));
            router.push('/error/server');
          }
        });
    },
    async delete(context: any, payload: InventoryDeleteDataState) {
      await axios
        .delete(getBaseUrl() + '/inventories', {
          headers: { auth: await getAccessToken() },
          data: {
            depotId: payload.depotId,
            inventoryDate: payload.inventoryDate,
            itemId: payload.itemId
          }
        })
        .then(() => {
          const list = context.state.detailListData;
          if (!list) return;
          context.commit(
            'setDetail',
            list.map((row: InventoryDetailsDataState) =>
              row.itemId === payload.itemId
                ? { ...row, inventoryQty: null }
                : row
            )
          );
        })
        .catch(async (e) => {
          if (e.response?.status === 401) {
            await handleAuthorizationError();
          } else if (e.response?.status && e.response.status < 500) {
            error(String(e.response.status));
            router.push('/inventory');
          } else {
            error(String(e.response?.status ?? 500));
            router.push('/error/server');
          }
          throw e;
        });
    },
    async fetchFromStock(
      context: any,
      payload: { depotId: number; inventoryDate: number }
    ) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .get(`${getBaseUrl()}/stocks?depotId=${payload.depotId}`, {
          headers: { auth: await getAccessToken() }
        })
        .then(async (response) => {
          const mapped = (response.data || []).map(
            (stock: {
              depotId: number;
              depotName: string;
              itemId: number;
              itemName: string;
              partCode: string;
              logicalStockQty: number;
            }) => ({
              depotId: stock.depotId,
              depotName: stock.depotName,
              inventoryDate: payload.inventoryDate,
              itemId: stock.itemId,
              itemName: stock.itemName,
              partCode: stock.partCode,
              logicalStockQty: stock.logicalStockQty,
              invLogicalStockQty: null,
              inventoryQty: null,
              registerUserId: null,
              registerUserName: null,
              updateUserId: null,
              updateUserName: null,
              confirmUserId: null,
              confirmUserName: null,
              cost: null
            })
          );
          await context.commit('setDetail', mapped);
          if (mapped.length > 0) {
            await context.commit('setDetailStatus', {
              status: context.state.status ?? 1,
              depotId: mapped[0].depotId,
              depotName: mapped[0].depotName,
              inventoryDate: String(mapped[0].inventoryDate)
            });
          } else {
            await context.commit('setDetailStatus', {
              status: context.state.status ?? 1,
              depotId: payload.depotId,
              inventoryDate: String(payload.inventoryDate)
            });
          }
          loadingInstance.close();
        })
        .catch((e) => {
          loadingInstance.close();
          if (e.response?.status === 401) {
            handleAuthorizationError();
          } else if (e.response?.status < 500) {
            error(e.response.status);
            router.push('/top');
          } else {
            error(String(e.response?.status ?? 500));
            router.push('/error/server');
          }
          throw e;
        });
    },
    async fetchDetail(context: any, payload: InventorySubmitDataState) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .get(
          `${getBaseUrl()}/inventories/details?depotId=${
            payload.depotId
          }&inventoryDate=${payload.inventoryDate}`,
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(async (response) => {
          await context.commit('setDetail', response.data);
          const first = response.data?.[0];
          if (first) {
            await context.commit('setDetailStatus', {
              status: context.state.status ?? 1,
              depotId: first.depotId,
              depotName: first.depotName,
              inventoryDate: first.inventoryDate
            });
          } else if (payload.depotId != null && payload.inventoryDate != null) {
            await context.commit('setDetailStatus', {
              status: context.state.status ?? 1,
              depotId: payload.depotId,
              inventoryDate: String(payload.inventoryDate)
            });
          }
          loadingInstance.close();
        })
        .catch((e) => {
          loadingInstance.close();
          if (e.response.status === 400) {
            error('400', '対象端末が存在しません。');
          } else if (e.response.status === 401) {
            handleAuthorizationError();
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/top');
          } else {
            router.push('/error/server');
          }
        });
    },
    async confirm(context: any, payload: InventoryDataState) {
      const loadingInstance = ElLoading.service({ fullscreen: true });
      await axios
        .post(
          `${getBaseUrl()}/inventories/confirm`,
          {
            depotId: payload.depotId,
            inventoryDate: payload.inventoryDate
          },
          {
            headers: { auth: await getAccessToken() }
          }
        )
        .then(async () => {
          router.push('/inventory');
          success('Success', '棚卸を確定しました。');
        })
        .catch((e) => {
          loadingInstance.close();
          if (e.response.status === 401) {
            handleAuthorizationError();
          } else if (e.response.status < 500) {
            error(e.response.status);
            router.push('/top');
          } else {
            router.push('/error/server');
          }
        });
    },
    async setFilterData(context: any, payload: InventoryFilterForm) {
      context.commit('setFilter', payload);
    }
  }
};
